// import { Progress } from "@vesatogo/grass-core"
import { ProgressBar } from "@blueprintjs/core"
import Duration from "components/Atoms/Duration"
import StyledLink from "components/Atoms/StyledLink"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import CustomReactTable from "components/Templates/CustomReactTable"
import dayjs from "dayjs"
import { Status, useAllBeatsQuery } from "generated/graphql"
import { startCase } from "lodash-es"
import React from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import displayTime from "utils/display-time"
import { inr } from "utils/formatters.utils"
import BeatStatusIcon from "../BeatStatusIcon"

function sortTime(timeStrings: string[]) {
  if (!timeStrings.length) return null
  return [...timeStrings].sort(
    (a, b) =>
      // The date can be any random date we are just interested in comparing
      // times
      new Date(`2022-02-01 ${a}`).getTime() -
      new Date(`2022-02-01 ${b}`).getTime()
  )
}
const MonitorListView = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const date = params.get("date") || dayjs().format("YYYY-MM-DD")
  const page = parseInt(params.get("page") || "0")
  const query = params.get("search")
  const { data, loading } = useAllBeatsQuery({
    variables: {
      date,
      first: 15,
      skip: page * 15,
      query,
    },
  })

  const columns = [
    {
      width: 31,
      required: true,
      show: true,
      resizable: false,
      sortable: false,
      accessor: "assignee.id",
    },
    {
      Header: "User",
      accessor: "assignee.fullName",
      aggregate: values => values[0],
      Aggregated({ value }) {
        return value
      },
      Cell() {
        return null
      },
    },
    {
      Header: "Survey Area",
      accessor: "assignment.route.surveyArea.properties.name",
      aggregate: values => values.length,
      Aggregated({ value }) {
        return `${value} areas`
      },
      Cell({ value, original }) {
        return <StyledLink to={original.id}>{value}</StyledLink>
      },
    },
    {
      Header: "Route",
      accessor: "assignment.route",
      aggregate: values => values.length,
      Aggregated({ value }) {
        return `${value} routes`
      },
      Cell({ value, original }) {
        return <StyledLink to={original.id}>{value.name}</StyledLink>
      },
    },
    {
      Header: "Start Time",
      accessor: "startTime",
      aggregate: startTimes => sortTime(startTimes)[0],
      Cell({ value }) {
        return displayTime(value)
      },
      Aggregated({ value }) {
        return displayTime(value)
      },
    },
    {
      Header: "End Time",
      accessor: "endTime",
      Cell({ value }) {
        return displayTime(value)
      },
      aggregate: endTimes => sortTime(endTimes)[endTimes.length - 1],
      Aggregated({ value }) {
        return displayTime(value)
      },
    },
    {
      Header: "Progress",
      accessor: "assignment",
      aggregate: values => {
        const aggregate = {
          beatVisitCount: 0,
          assignedRoutePointCount: 0,
        }
        for (const value of values) {
          aggregate.beatVisitCount += value.beatVisitCount
          aggregate.assignedRoutePointCount += value.assignedRoutePointCount
        }
        return {
          ...aggregate,
          percent: aggregate.beatVisitCount / aggregate.assignedRoutePointCount,
        }
      },
      Aggregated({ value }) {
        const percent = value.percent
        const beatVisitCount = value.beatVisitCount
        const assignedRoutePointCount = value.assignedRoutePointCount
        const progress = Math.round(percent * 100)
        return (
          <>
            <ProgressBar
              stripes
              intent={
                progress >= 75
                  ? "primary"
                  : progress > 25 && progress < 75
                  ? "warning"
                  : "danger"
              }
              value={percent}
            />
            <div className="text-gray-500">
              {beatVisitCount} / {assignedRoutePointCount}
            </div>
          </>
        )
      },
      Cell({ value }) {
        const percent = value.beatVisitCount / value.assignedRoutePointCount
        const progress = Math.round(percent * 100)

        return (
          <>
            <ProgressBar
              intent={
                progress >= 75
                  ? "primary"
                  : progress > 25 && progress < 75
                  ? "warning"
                  : "danger"
              }
              stripes
              value={progress}
            />
            <div className="text-gray-500">
              {value.beatVisitCount} / {value.assignedRoutePointCount}
            </div>
          </>
        )
      },
    },
    {
      Header: "Average Time",
      accessor: "summary.durationAvg",
      aggregate: durations => {
        let hours = 0,
          minutes = 0,
          seconds = 0

        for (const duration of durations) {
          hours += duration?.hours || 0
          minutes += duration?.minutes || 0
          seconds += duration?.seconds || 0
        }

        return {
          hours: Math.round(hours / durations.length),
          minutes: Math.round(minutes / durations.length),
          seconds: Math.round(seconds / durations.length),
        }
      },
      Aggregated({ value }) {
        if (!value) return "--:--"
        return <Duration duration={value} />
      },
      Cell({ value }) {
        if (!value) return "--:--"
        return <Duration duration={value} />
      },
    },
    {
      accessor: "summary.placedOrderCount",
      Header: "No. Of Orders",
      aggregate(values) {
        return values.reduce((acc, value) => acc + value, 0)
      },
      Aggregated({ value }) {
        return value
      },
      Cell({ value }) {
        return value?.toLocaleString()
      },
    },
    {
      accessor: "summary.secondarySales",
      Header: "Order Value",
      aggregate(values) {
        return values.reduce((acc, value) => acc + value, 0)
      },
      Aggregated({ value }) {
        return inr(value)
      },
      Cell({ value }) {
        return inr(value)
      },
    },
    {
      accessor: "summary.dropSize",
      Header: "Drop Size",
      aggregate(values) {
        return values.reduce((acc, value) => acc + value, 0)
      },
      Aggregated({ value }) {
        return inr(value)
      },
      Cell({ value }) {
        return inr(value)
      },
    },
    {
      Header: "Status",
      accessor: "status",
      width: 270,
      aggregate: values => values,
      Aggregated({ subRows }) {
        return (
          <OverFlowMenu
            onClick={item => navigate(item._original?.id)}
            tagIcon={"dot"}
            icon="dot"
            items={subRows}
            renderText={item => startCase(item._original?.status)}
            getIntent={item => {
              switch (item._original?.status) {
                case Status.COMPLETED:
                  return "success"
                case Status.PAUSE:
                  return "danger"
                case Status.NOT_STARTED:
                  return "warning"
                default:
                  return "primary"
              }
            }}
          />
        )
      },
      Cell({ value }) {
        return <BeatStatusIcon status={value} />
      },
    },
  ]

  return (
    <div className="shadow-sm rounded-lg bg-white overflow-hidden">
      <CustomReactTable
        loading={loading}
        loadingText="Fetching Beats..."
        getTrProps={(_, row) => {
          const status = row?.original?.status

          if (status === Status.COMPLETED) {
            return {
              className: "bg-green-50",
            }
          } else if (status === Status.PAUSE) {
            return {
              className: "bg-red-100",
            }
          } else if (status === Status.NOT_STARTED) {
            return {
              className: "bg-yellow-50",
            }
          }
          return {
            className: "",
          }
        }}
        className="rounded-md text-gray-700"
        columns={columns}
        data={data?.allBeats}
        pageSize={10}
        pivotBy={["assignee.id"]}
      />
    </div>
  )
}

export default MonitorListView
